import { Injectable, inject } from '@angular/core';

import { CookieDBService, CookieList, CookieName, MenuContentItem } from '@frontend/vanilla/core';
import { toNumber } from 'lodash-es';
import { ReplaySubject } from 'rxjs';

import { AccountMenuDataService } from './account-menu-data.service';
import { AccountMenuTaskStatus } from './account-menu.models';

/** @stable */
@Injectable({
    providedIn: 'root',
})
export class AccountMenuTasksService {
    expanded: boolean;
    readonly displayItems: ReplaySubject<MenuContentItem[]> = new ReplaySubject<MenuContentItem[]>();

    private items: MenuContentItem[] = [];
    private readonly db: CookieList<string>;

    private readonly accountMenuDataService = inject(AccountMenuDataService);
    private readonly cookieDBService = inject(CookieDBService);

    constructor() {
        this.db = this.cookieDBService.createList(CookieName.AmHiddenTasks);

        this.accountMenuDataService.content.subscribe(() => {
            const item = this.accountMenuDataService.getItem('tasks');

            if (item?.children) {
                this.items = [
                    ...this.items.filter((existingItem: MenuContentItem) =>
                        item.children.some((child: MenuContentItem) => child.name === existingItem.name),
                    ),
                    ...item.children.filter(
                        (child: MenuContentItem) => !this.items.some((existingItem: MenuContentItem) => existingItem.name === child.name),
                    ),
                ];
                this.refreshItems();
            }
        });
    }

    get totalCount(): number {
        return this.items.length;
    }

    get totalUrgentCount(): number {
        return this.items.filter((x: MenuContentItem) => x.parameters['status'] === AccountMenuTaskStatus.URGENT).length;
    }

    /** Hides task items. */
    hide(items: MenuContentItem[]) {
        items.forEach((item: MenuContentItem) => this.db.insert(item.name));
        this.refreshItems();
    }

    /** Shows all tasks. */
    showAllHidden() {
        this.db.deleteAll();
        this.refreshItems();
    }

    /** Indicates if there are hidden tasks. */
    hasHidden(): boolean {
        return this.db.getAll().length > 0;
    }

    /** Updates task item. */
    update(item: MenuContentItem) {
        this.items[this.items.indexOf(item)] = item;
        this.refreshItems();
    }

    /** Determines if item is urgent. */
    isUrgent(item: MenuContentItem): boolean {
        return item.parameters['status'] === AccountMenuTaskStatus.URGENT;
    }

    private refreshItems() {
        this.displayItems.next(
            this.items.filter((t: MenuContentItem) => !this.db.getAll().includes(t.name)).sort((a, b) => this.getItemOrder(a) - this.getItemOrder(b)),
        );
    }

    private getItemOrder(item: MenuContentItem): number {
        const order = toNumber(item.parameters['order'] ?? '10');
        const status = item.parameters['status'] ?? AccountMenuTaskStatus.PENDING;
        const statusMultiplier = status === AccountMenuTaskStatus.URGENT ? 1 : 100;

        return order * statusMultiplier;
    }
}
